import { env } from '@/config/env'

interface PostHogUser {
  id: string
  email?: string
  name?: string
  [key: string]: unknown
}

interface PostHogConfig {
  apiKey?: string
  apiHost: string
  enabled: boolean
}

interface PostHogLike {
  init: (apiKey: string, config: Record<string, unknown>) => void
  identify: (id: string, properties?: Record<string, unknown>) => void
  reset: () => void
  capture: (event: string, properties?: Record<string, unknown>) => void
  isFeatureEnabled: (flagKey: string) => boolean | undefined
  getFeatureFlag: (flagKey: string) => string | boolean | undefined
  debug: () => void
}

const config: PostHogConfig = {
  apiKey: env.VITE_POSTHOG_KEY,
  apiHost: env.VITE_POSTHOG_HOST,
  enabled: env.VITE_ENABLE_ANALYTICS,
}

let posthogInstance: PostHogLike | null = null

const isProd = import.meta.env.PROD
const isDev = import.meta.env.DEV

export async function initPostHog() {
  if (posthogInstance || !config.apiKey || !config.enabled) {
    return
  }

  try {
    const posthog = await import('posthog-js')
    const ph = posthog.default as PostHogLike
    posthogInstance = ph

    ph.init(config.apiKey, {
      api_host: config.apiHost,
      capture_pageview: true,
      capture_pageleave: true,
      persistence: 'localStorage+cookie',
      autocapture: isProd,
      disable_session_recording: !isProd,
      loaded: () => {
        if (isDev) {
          ph.debug()
        }
      },
    })
  } catch (error) {
    console.error('Failed to initialize PostHog:', error)
  }
}

export function identify(user: PostHogUser) {
  if (!posthogInstance) return

  posthogInstance.identify(user.id, {
    email: user.email,
    name: user.name,
    ...user,
  })
}

export function reset() {
  if (!posthogInstance) return
  posthogInstance.reset()
}

export function track(event: string, properties?: Record<string, unknown>) {
  if (!posthogInstance) {
    console.log(`[Analytics] ${event}`, properties)
    return
  }

  posthogInstance.capture(event, properties)
}

export function isFeatureEnabled(flagKey: string): boolean {
  if (!posthogInstance) return false
  return posthogInstance.isFeatureEnabled(flagKey) ?? false
}

export function getFeatureFlag(flagKey: string): string | boolean | undefined {
  if (!posthogInstance) return undefined
  return posthogInstance.getFeatureFlag(flagKey)
}

export function testPostHogIntegration() {
  track('test_event', { source: 'integration_test' })
  console.log('PostHog test event sent')
}

export const analytics = {
  track,
  identify,
  reset,
  isFeatureEnabled,
  getFeatureFlag,
}
